/*
 * <roll-number>: a spec figure such as "800+" whose digits roll up like an odometer, left to right, the first time it
 * scrolls into view. Markup and fallbacks are in snippets/spec-value.liquid; styles are in brand.css ("Motion").
 *
 * Each digit keeps an invisible copy of its final glyph, so the number takes exactly the width it has without motion
 * and nothing around it moves. Screen readers get the original text; the rolling copy is aria-hidden.
 */
if (!customElements.get('roll-number')) {
  // brand.css reveals figures that are still waiting for this script after 2s; resetting them to zero would flicker
  const FALLBACK_MS = 2000;

  customElements.define(
    'roll-number',
    class RollNumber extends HTMLElement {
      connectedCallback() {
        if (!this.built) {
          const text = this.textContent.trim();
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          // The theme editor re-renders sections on every keystroke, so figures stay still there
          const editor = window.Shopify && window.Shopify.designMode;
          const late = performance.now() > FALLBACK_MS;
          if (reduce || editor || late || !/\d/.test(text) || !('IntersectionObserver' in window)) return;
          this.built = true;
          this.dataset.text = text;
          this.build(text);
        }
        if (this.classList.contains('is-rolling')) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            // Two frames so the start position is painted before the transition begins
            requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-rolling')));
          },
          { threshold: 0.6 }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      // Start after every character of the figures before this one in the same row, plus a beat between figures
      startStep() {
        const row = this.closest('.spec-row');
        if (!row) return 0;
        let step = 0;
        for (const figure of row.querySelectorAll('roll-number[data-text]')) {
          if (figure === this) break;
          step += figure.dataset.text.length + 1;
        }
        return step;
      }

      build(text) {
        let step = this.startStep();
        const readable = document.createElement('span');
        readable.className = 'visually-hidden';
        readable.textContent = text;

        const roll = document.createElement('span');
        roll.className = 'roll';
        roll.setAttribute('aria-hidden', 'true');

        // Words stay whole and wrap at their spaces, like the plain text would
        text.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            roll.append(' ');
            step += 1;
            return;
          }
          const word = document.createElement('span');
          word.className = 'roll__word';
          Array.from(part).forEach((char) => word.appendChild(this.slot(char, step++)));
          roll.appendChild(word);
        });

        this.replaceChildren(readable, roll);
      }

      slot(char, step) {
        const slot = document.createElement('span');
        slot.style.setProperty('--roll-i', step);

        if (!/\d/.test(char)) {
          slot.className = 'roll__char';
          slot.textContent = char;
          return slot;
        }

        // One full turn of the wheel, then on to the final digit
        const sequence = '0123456789'.split('').concat('0123456789'.slice(0, Number(char) + 1).split(''));
        const strip = document.createElement('span');
        strip.className = 'roll__strip';
        strip.style.setProperty('--roll-to', `${((sequence.length - 1) / sequence.length) * -100}%`);
        sequence.forEach((digit) => {
          const line = document.createElement('span');
          line.textContent = digit;
          strip.appendChild(line);
        });

        const ghost = document.createElement('span');
        ghost.className = 'roll__ghost';
        ghost.textContent = char;

        slot.className = 'roll__digit';
        slot.append(ghost, strip);
        return slot;
      }
    }
  );
}
